import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { T } from "@/content/site";
import { api } from "@/lib/api";

const F = (v) => (v ? "filled" : "");

export default function PrivateLabelForm() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", country: "", whatsapp: "", volume: "", market: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/private-label", form);
      toast.success(t({ es: "Solicitud recibida. Te contactamos en breve.", en: "Request received. We'll contact you shortly." }));
      setForm({ name: "", company: "", email: "", country: "", whatsapp: "", volume: "", market: "", message: "" });
    } catch (err) {
      toast.error(t({ es: "No pudimos enviar la solicitud.", en: "We couldn't send the request." }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="private-label-form" data-testid="pl-form-section" className="relative bg-[#0B0B0B] py-[140px] md:py-[180px] border-t border-white/5">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline mb-8">
              <span className="inline-block w-8 h-px bg-[color:var(--sacrvm-gold)] align-middle mr-3" />
              {t(T.privateLabelForm.overline)}
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="font-serif text-white text-[42px] md:text-[64px] leading-[1] tracking-[-0.01em]"
            >
              {t(T.privateLabelForm.title)}
            </motion.h2>
            <p className="mt-8 text-[color:var(--sacrvm-text-muted)] font-serif italic text-[16px] max-w-[420px] leading-[1.6]">
              {t(T.privateLabelForm.subtitle)}
            </p>
          </div>

          <div className="col-span-12 md:col-span-7 md:pl-10">
            <form onSubmit={submit} data-testid="pl-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              <div className={"field " + F(form.name)}>
                <label>{t({ es: "Nombre", en: "Name" })}</label>
                <input data-testid="pl-name" value={form.name} onChange={set("name")} required />
              </div>
              <div className={"field " + F(form.company)}>
                <label>{t({ es: "Empresa", en: "Company" })}</label>
                <input data-testid="pl-company" value={form.company} onChange={set("company")} />
              </div>
              <div className={"field " + F(form.email)}>
                <label>{t({ es: "Correo", en: "Email" })}</label>
                <input data-testid="pl-email" type="email" value={form.email} onChange={set("email")} required />
              </div>
              <div className={"field " + F(form.country)}>
                <label>{t({ es: "País", en: "Country" })}</label>
                <input data-testid="pl-country" value={form.country} onChange={set("country")} />
              </div>
              <div className={"field " + F(form.whatsapp)}>
                <label>WhatsApp</label>
                <input data-testid="pl-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} />
              </div>
              <div className={"field " + F(form.volume)}>
                <label>{t(T.privateLabelForm.fields.volume)}</label>
                <input data-testid="pl-volume" value={form.volume} onChange={set("volume")} />
              </div>
              <div className={"field md:col-span-2 " + F(form.market)}>
                <label>{t(T.privateLabelForm.fields.market)}</label>
                <input data-testid="pl-market" value={form.market} onChange={set("market")} />
              </div>
              <div className={"field md:col-span-2 " + F(form.message)}>
                <label>{t({ es: "Mensaje", en: "Message" })}</label>
                <textarea data-testid="pl-message" rows={3} value={form.message} onChange={set("message")} />
              </div>
              <div className="md:col-span-2 mt-10">
                <button data-testid="pl-submit" disabled={loading} className="btn-sharp gold">
                  {loading ? t({ es: "Enviando…", en: "Sending…" }) : t(T.privateLabelForm.submit)}
                  <ArrowRight size={14} className="arrow" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
